import axios from 'axios';
import { extractErrorMessage } from '../utils/errorHandler';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/v1';

let refreshTokenRequest = null;
let inactivityTimer = null;
let proactiveRefreshTimer = null;
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
const PROACTIVE_REFRESH_INTERVAL = 3 * 60 * 1000; // Refresh token every 3 minutes proactively

// Function to handle proactive token refresh
export const startProactiveRefreshTimer = () => {
  // Clear existing timer
  if (proactiveRefreshTimer) {
    clearInterval(proactiveRefreshTimer);
  }
  
  console.log('[Auth] Proactive refresh enabled: every 3 minutes');
  
  // Set interval to refresh token every 3 minutes
  proactiveRefreshTimer = setInterval(async () => {
    await proactiveRefreshAccessToken();
  }, PROACTIVE_REFRESH_INTERVAL);
};

// Function to handle inactivity logout
export const startInactivityTimer = () => {
  // Clear existing timer
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }
  
  // Set new timer - logs out user after 15 minutes of no activity
  inactivityTimer = setTimeout(() => {
    console.log('[Auth] Inactivity timeout - logging out');
    // Clear tokens and log out
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('companyProfile');
    
    // Clear timers
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (proactiveRefreshTimer) clearInterval(proactiveRefreshTimer);
    
    // Redirect to login
    window.location.href = '/login';
  }, INACTIVITY_TIMEOUT);
};

// Function to refresh the access token (for proactive refresh - silent failure)
const proactiveRefreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      console.warn('[Auth] No refresh token for proactive refresh');
      return null;
    }

    const response = await axios.post(`${API_URL}/refresh/`, {
      refresh: refreshToken,
    });

    const newAccessToken = response.data.access || response.data.access_token;
    if (newAccessToken) {
      console.log('[Auth] ✓ Token proactively refreshed');
      localStorage.setItem('token', newAccessToken);
      startInactivityTimer(); // Reset inactivity timer on successful refresh
      return newAccessToken;
    }
    
    console.warn('[Auth] No access token in refresh response');
    return null;
  } catch (error) {
    // Note: /refresh/ endpoint may not exist on backend, which is okay for now
    // Token will be re-issued on demand when 401 is received
    return null;
  }
};

// Function to refresh the access token (for request retry - will logout on failure)
const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      console.error('[Auth] ✗ No refresh token available - logging out');
      // Clear tokens and log out
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('companyProfile');
      
      // Clear timers
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (proactiveRefreshTimer) clearInterval(proactiveRefreshTimer);
      
      // Redirect to login
      setTimeout(() => {
        alert('Session expired. Please login again.');
        window.location.href = '/login';
      }, 100);
      
      throw new Error('No refresh token available');
    }

    console.log('[Auth] Attempting to refresh token via /refresh/ endpoint...');
    const response = await axios.post(`${API_URL}/refresh/`, {
      refresh: refreshToken,
    });

    const newAccessToken = response.data.access || response.data.access_token;
    if (newAccessToken) {
      console.log('[Auth] ✓ Token refreshed from 401 response');
      localStorage.setItem('token', newAccessToken);
      startInactivityTimer(); // Reset inactivity timer on successful refresh
      return newAccessToken;
    }
    
    throw new Error('No access token in refresh response');
  } catch (error) {
    console.error('[Auth] ✗ Token refresh FAILED:', error.response?.status, error.response?.statusText);
    console.log('[Auth] → Backend may not have /refresh/ endpoint implemented');
    console.log('[Auth] → Logging out user and redirecting to login');
    
    // Clear tokens and log out
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('companyProfile');
    
    // Clear timers
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (proactiveRefreshTimer) clearInterval(proactiveRefreshTimer);
    
    // Redirect to login
    setTimeout(() => {
      alert('Your session has expired. Please login again.');
      window.location.href = '/login';
    }, 100);
    
    throw new Error(extractErrorMessage(error));
  }
};

// Create a function to setup interceptors on any axios instance
export const setupAuthInterceptors = (apiClient, serviceName = 'API') => {
  // Request interceptor - attach token and reset inactivity timer
  apiClient.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        const cleanToken = token.replace(/^Bearer\s+/, '');
        config.headers.Authorization = `Bearer ${cleanToken}`;
      }
      // Reset inactivity timer on every API request
      startInactivityTimer();
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

// Response interceptor - handle 401 and refresh token
  apiClient.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Only retry on 401 (unauthorized) - not on other errors like 404
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // If a refresh is already in progress, wait for it
        if (refreshTokenRequest) {
          try {
            const newToken = await refreshTokenRequest;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        // Start a new refresh request
        refreshTokenRequest = refreshAccessToken()
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          })
          .finally(() => {
            refreshTokenRequest = null;
          });

        return refreshTokenRequest;
      }

      // Don't log out on 404 or other errors - let them propagate
      return Promise.reject(error);
    }
  );

  return apiClient;
};

// Export function to manually clear all timers (for logout)
export const clearAuthTimers = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
  if (proactiveRefreshTimer) {
    clearInterval(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
};
